import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import type { OnyxChip } from '@onyx/angular';
import {
  FormHelper,
  OnyxChipsComponent,
  OnyxSectionComponent,
  OnyxToggleComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { linkedChipSignal } from '../../../../../common/utilities/linked-chip-signal';
import type { ContractorBranchForm } from '../contractor-branches-form/contractor-branch-form-modal/contractor-branch-form-modal.component';
import { ContractorShippingTypeFormComponent } from '../contractor-shipping-type-form/contractor-shipping-type-form.component';

export type ContractorInvoiceDocumentsFormArray = ReturnType<
  typeof ContractorInvoiceDocumentsFormComponent.buildForm
>;

export type ContractorInvoiceDocumentsFormGroup = ReturnType<
  typeof ContractorInvoiceDocumentsFormComponent.buildInvoiceDocumentsForm
>;
export type ContractorInvoiceDocumentsForm = ReturnType<
  ContractorInvoiceDocumentsFormGroup['getRawValue']
>;

@Component({
  selector: 'app-contractor-invoice-documents-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxChipsComponent,
    ContractorShippingTypeFormComponent,
    OnyxToggleComponent,
    OnyxTooltipDirective,
  ],
  templateUrl: './contractor-invoice-documents-form.component.html',
  styleUrl: './contractor-invoice-documents-form.component.scss',
})
export class ContractorInvoiceDocumentsFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);

  public readonly form = input.required<ContractorInvoiceDocumentsFormArray>();
  public readonly branches = input.required<ContractorBranchForm[]>();

  protected readonly I18N = 'contractors.contractorForm.invoiceDocuments';

  protected isEnabled = signal(false);
  protected chips = computed<OnyxChip<string>[]>(() =>
    this.branches().map((branch) => ({
      name: branch.name!,
      value: branch.uuid!,
    })),
  );

  protected selectedBranch = linkedChipSignal(() => this.chips());
  protected selectedBranchForm = computed(() =>
    this.form().controls.find(
      ({ value }) => value.branchUuid === this.selectedBranch()[0],
    ),
  );
  protected mainBranchForm = computed(() =>
    this.form().controls.find(
      ({ value }) => value.branchUuid === this.branches()[0].uuid,
    )!,
  );

  public ngOnInit(): void {
    const form = this.form();
    this.isEnabled.set(
      form.getRawValue().some(({ shippingType }) => {
        const { internalPortal } = shippingType;
        return (
          shippingType.emails.length ||
          Object.values(internalPortal ?? {}).some((item) => item != null) ||
          shippingType.traditionalMail
        );
      }),
    );

    effect(
      () => {
        const enabled = this.isEnabled();
        ValidationHelper.toggleControls(form, { enabled, reset: false });

        if (!enabled) {
          FormHelper.reset(this.mainBranchForm());
          for (const control of form.controls) {
            if (control === this.mainBranchForm()) continue;
            form.removeAt(form.controls.indexOf(control));
          }
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const branches = new Set(this.branches().map((branch) => branch.uuid!));
        for (const control of form.controls) {
          if (branches.has(control.controls.branchUuid.value)) continue;
          form.removeAt(form.controls.indexOf(control));
        }
      },
      { injector: this.injector },
    );
  }

  public focusInvalidForm(): void {
    if (this.selectedBranchForm()?.invalid) return;

    for (const branch of this.form().controls) {
      if (branch.valid) continue;

      this.selectedBranch.set([branch.controls.branchUuid.value]);
      return;
    }
  }

  protected addBranchForm(): void {
    const template = this.mainBranchForm().getRawValue();
    const branchUuid = this.selectedBranch()[0];

    this.form().push(
      ContractorInvoiceDocumentsFormComponent.buildInvoiceDocumentsForm(
        this.fb,
        branchUuid,
      ),
    );

    const branchForm = this.form().at(-1);
    for (const _ of template.shippingType.emails) {
      branchForm.controls.shippingType.controls.emails.push(
        ContractorShippingTypeFormComponent.buildEmailForm(this.fb),
      );
    }

    branchForm.setValue({ ...template, branchUuid });
  }

  protected removeBranchForm(): void {
    const branchForm = this.selectedBranchForm();
    if (!branchForm) return;

    const index = this.form().controls.indexOf(branchForm);
    this.form().removeAt(index);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<ContractorInvoiceDocumentsFormGroup>([]);
  }

  public static buildInvoiceDocumentsForm(
    fb: NonNullableFormBuilder,
    branchUuid: string,
  ) {
    return fb.group({
      branchUuid: fb.control(branchUuid, [Validators.required]),
      shippingType: ContractorShippingTypeFormComponent.buildForm(fb),
    });
  }
}
